import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash, ArrowsClockwise, Microphone, StopCircle, MagicWand } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { formatCurrency } from "../lib/currency";
import { useAuth } from "../lib/auth";
import { StandardReports } from "../components/StandardReports";

const TABS = ["Overview", "Chart of Accounts", "Journals", "Invoices", "Reports"];

function StatPill({ label, value }) {
  return (
    <div className="border-r border-zinc-200 last:border-r-0 px-6 py-4">
      <div className="label-sm text-zinc-500">{label}</div>
      <div className="font-display font-black text-2xl tnum mt-1">{value}</div>
    </div>
  );
}

const fmt$ = (n, cur = "USD") => formatCurrency(n, cur);

const useTenantCurrency = () => {
  const { user } = useAuth();
  return user?.tenant?.preferred_currency || "OMR";
};

export default function FinancePage() {
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "Overview");
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const cur = useTenantCurrency();

  const refresh = async () => {
    const [s, a, j, i] = await Promise.all([
      api.get("/finance/summary").then((r) => r.data),
      api.get("/finance/accounts").then((r) => r.data),
      api.get("/finance/journals").then((r) => r.data),
      api.get("/finance/invoices").then((r) => r.data),
    ]);
    setSummary(s); setAccounts(a); setJournals(j); setInvoices(i);
  };

  useEffect(() => { refresh().catch(() => {}); }, []);

  return (
    <div className="p-6 sm:p-8" data-testid="finance-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / 01 · FINANCE</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Finance.</h1>
        </div>
        <button onClick={refresh} className="text-xs font-mono text-zinc-500 hover:text-zinc-950 flex items-center gap-1" data-testid="finance-refresh">
          <ArrowsClockwise size={12} /> REFRESH
        </button>
      </div>

      {/* Stats */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 border border-zinc-200 mb-6">
          <StatPill label="AR Total" value={fmt$(summary.ar_total, cur)} />
          <StatPill label="AP Total" value={fmt$(summary.ap_total, cur)} />
          <StatPill label="AR Open" value={fmt$(summary.ar_open, cur)} />
          <StatPill label="AP Open" value={fmt$(summary.ap_open, cur)} />
          <StatPill label="Net" value={fmt$(summary.net_position, cur)} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-zinc-950 mb-0">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`finance-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-xs font-semibold tracking-[0.1em] uppercase border-r border-zinc-950 transition-colors ${
              tab === t ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="border-x border-b border-zinc-950">
        {tab === "Overview" && <Overview summary={summary} accounts={accounts} invoices={invoices} />}
        {tab === "Chart of Accounts" && <Accounts accounts={accounts} onChange={refresh} />}
        {tab === "Journals" && <Journals journals={journals} accounts={accounts} onChange={refresh} />}
        {tab === "Invoices" && <Invoices invoices={invoices} onChange={refresh} />}
        {tab === "Reports" && <StandardReports module="finance" />}
      </div>
    </div>
  );
}

function Overview({ summary, accounts, invoices }) {
  const cur = useTenantCurrency();
  const grouped = accounts.reduce((acc, a) => {
    acc[a.type] = (acc[a.type] || 0) + (a.balance || 0);
    return acc;
  }, {});
  const overdue = invoices.filter((i) => i.status === "overdue");

  return (
    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200">
      <div className="bg-white p-5">
        <div className="label-sm text-zinc-500 mb-3">Balances by Type</div>
        <div className="space-y-2">
          {["asset", "liability", "equity", "revenue", "expense"].map((t) => (
            <div key={t} className="flex justify-between text-sm border-b border-zinc-100 pb-1">
              <span className="capitalize text-zinc-600">{t}</span>
              <span className="tnum font-semibold">{fmt$(grouped[t] || 0, cur)}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white p-5">
        <div className="label-sm text-zinc-500 mb-3">Overdue Invoices · {overdue.length}</div>
        {overdue.length === 0 && <div className="text-xs text-zinc-400 font-mono">NONE</div>}
        {overdue.map((i) => (
          <div key={i.id} className="flex justify-between text-sm py-1 border-b border-zinc-100">
            <span>{i.party}</span>
            <span className="tnum text-[#D0021B]">{fmt$(i.amount, i.currency || cur)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Accounts({ accounts, onChange }) {
  const cur = useTenantCurrency();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState("asset");

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/accounts", { code, name, type });
      toast.success("Account created");
      setCode(""); setName("");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    await api.delete(`/finance/accounts/${id}`);
    toast.success("Account deleted");
    onChange();
  };

  return (
    <div>
      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-[120px_1fr_140px_auto] gap-px bg-zinc-200 border-b border-zinc-950" data-testid="account-create-form">
        <input value={code} onChange={(e) => setCode(e.target.value)} required placeholder="Code" data-testid="account-code-input" className="bg-white px-4 py-3 text-sm outline-none" />
        <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Account name" data-testid="account-name-input" className="bg-white px-4 py-3 text-sm outline-none" />
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="account-type-select" className="bg-white px-4 py-3 text-sm outline-none">
          {["asset", "liability", "equity", "revenue", "expense"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 hover:bg-[#FF3E00] flex items-center justify-center gap-1" data-testid="account-create-btn">
          <Plus size={14} /> ADD
        </button>
      </form>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">Code</th>
            <th className="text-left px-4 py-2.5">Name</th>
            <th className="text-left px-4 py-2.5">Type</th>
            <th className="text-right px-4 py-2.5">Balance</th>
            <th className="w-10" />
          </tr>
        </thead>
        <tbody data-testid="accounts-tbody">
          {accounts.map((a) => (
            <tr key={a.id} className="border-b border-zinc-100 hover:bg-zinc-50">
              <td className="px-4 py-2.5 tnum">{a.code}</td>
              <td className="px-4 py-2.5">{a.name}</td>
              <td className="px-4 py-2.5 capitalize text-zinc-500">{a.type}</td>
              <td className="px-4 py-2.5 text-right tnum">{fmt$(a.balance, cur)}</td>
              <td className="px-2"><button onClick={() => del(a.id)} className="text-zinc-400 hover:text-[#D0021B]"><Trash size={14} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Journals({ journals, accounts, onChange }) {
  const cur = useTenantCurrency();
  const [memo, setMemo] = useState("");
  const [isIntercompany, setIsIntercompany] = useState(false);
  const [counterpartyTenantId, setCounterpartyTenantId] = useState("");
  const newLine = () => ({ key: `ln-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, account_code: "", debit: 0, credit: 0 });
  const [lines, setLines] = useState([newLine(), newLine()]);

  const applyVoiceDraft = (draft) => {
    if (!draft) return;
    setMemo(draft.memo || "");
    if (Array.isArray(draft.lines) && draft.lines.length > 0) {
      setLines(draft.lines.map((l, i) => ({ key: `voice-${i}-${Date.now()}`, account_code: l.account_code, debit: l.debit || 0, credit: l.credit || 0 })));
    }
    if (draft.is_intercompany) setIsIntercompany(true);
    toast.success("Voice draft applied — review the lines and post");
  };

  const setLine = (i, k, v) => {
    const next = [...lines];
    next[i] = { ...next[i], [k]: k === "account_code" ? v : Number(v) || 0 };
    setLines(next);
  };

  const debit = lines.reduce((s, l) => s + (l.debit || 0), 0);
  const credit = lines.reduce((s, l) => s + (l.credit || 0), 0);
  const balanced = Math.abs(debit - credit) < 0.001 && debit > 0;

  const post = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/journals", {
        memo, lines,
        is_intercompany: isIntercompany,
        counterparty_tenant_id: isIntercompany && counterpartyTenantId ? counterpartyTenantId : null,
      });
      toast.success(isIntercompany ? "Intercompany journal posted (will be eliminated in consolidation)" : "Journal posted");
      setMemo(""); setLines([newLine(), newLine()]);
      setIsIntercompany(false); setCounterpartyTenantId("");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <VoiceJournalPanel onApply={applyVoiceDraft} />
      <form onSubmit={post} className="p-5 border-b border-zinc-950 space-y-3" data-testid="journal-form">
        <input value={memo} onChange={(e) => setMemo(e.target.value)} required placeholder="Memo (e.g., Cash sale to Acme Corp)" data-testid="journal-memo-input" className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none" />
        <div className="grid grid-cols-[1fr_120px_120px] gap-px bg-zinc-200">
          <div className="bg-zinc-50 label-sm text-zinc-500 px-3 py-2">Account</div>
          <div className="bg-zinc-50 label-sm text-zinc-500 px-3 py-2 text-right">Debit</div>
          <div className="bg-zinc-50 label-sm text-zinc-500 px-3 py-2 text-right">Credit</div>
          {lines.map((l, i) => (
            <div key={l.key} className="contents">
              <select value={l.account_code} onChange={(e) => setLine(i, "account_code", e.target.value)} required data-testid={`journal-line-${i}-account`} className="bg-white px-3 py-2 text-sm outline-none">
                <option value="">— select —</option>
                {accounts.map((a) => <option key={a.id} value={a.code}>{a.code} · {a.name}</option>)}
              </select>
              <input type="number" step="0.01" value={l.debit} onChange={(e) => setLine(i, "debit", e.target.value)} data-testid={`journal-line-${i}-debit`} className="bg-white px-3 py-2 text-sm text-right tnum outline-none" />
              <input type="number" step="0.01" value={l.credit} onChange={(e) => setLine(i, "credit", e.target.value)} data-testid={`journal-line-${i}-credit`} className="bg-white px-3 py-2 text-sm text-right tnum outline-none" />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-wrap">
            <button type="button" onClick={() => setLines([...lines, newLine()])} className="text-xs font-mono text-zinc-500 hover:text-zinc-950">+ ADD LINE</button>
            <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid="journal-intercompany-toggle">
              <input
                type="checkbox"
                checked={isIntercompany}
                onChange={(e) => setIsIntercompany(e.target.checked)}
                className="accent-[#FF3E00]"
                data-testid="journal-intercompany-checkbox"
              />
              <span className="font-mono tracking-wider">INTERCOMPANY</span>
            </label>
            {isIntercompany && (
              <input
                value={counterpartyTenantId}
                onChange={(e) => setCounterpartyTenantId(e.target.value)}
                placeholder="Counterparty tenant_id"
                className="border border-zinc-950 bg-white px-2 py-1 text-xs w-60"
                data-testid="journal-counterparty-input"
              />
            )}
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="tnum">DR {fmt$(debit, cur)} / CR {fmt$(credit, cur)}</span>
            <button type="submit" disabled={!balanced} className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2 hover:bg-[#FF3E00] disabled:opacity-30" data-testid="journal-submit-btn">
              POST JOURNAL
            </button>
          </div>
        </div>
      </form>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">Date</th>
            <th className="text-left px-4 py-2.5">Memo</th>
            <th className="text-right px-4 py-2.5">Total</th>
            <th className="text-left px-4 py-2.5">By</th>
          </tr>
        </thead>
        <tbody data-testid="journals-tbody">
          {journals.map((j) => (
            <tr key={j.id} className="border-b border-zinc-100">
              <td className="px-4 py-2.5 tnum text-zinc-500">{j.date}</td>
              <td className="px-4 py-2.5">{j.memo}</td>
              <td className="px-4 py-2.5 text-right tnum font-semibold">{fmt$(j.total, cur)}</td>
              <td className="px-4 py-2.5 text-zinc-500 text-xs">{j.created_by}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Invoices({ invoices, onChange }) {
  const cur = useTenantCurrency();
  const [type, setType] = useState("AR");
  const [party, setParty] = useState("");
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [vatCategory, setVatCategory] = useState("standard");
  const [isIntercompany, setIsIntercompany] = useState(false);
  const [counterpartyTenantId, setCounterpartyTenantId] = useState("");
  const [siblings, setSiblings] = useState([]);
  const [autoPairHint, setAutoPairHint] = useState(null);
  const [anomalyIds, setAnomalyIds] = useState({});          // { id: {severity, reasons[]} }

  useEffect(() => {
    api.get("/consolidation/my-group-members").then((r) => {
      setSiblings(r.data?.tenants || []);
    }).catch(() => setSiblings([]));
    // Load anomaly flags for the invoice list
    api.get("/anomaly/scan?days=90&kind=invoice&limit=200").then((r) => {
      const map = {};
      (r.data?.flags || []).forEach((f) => { map[f.id] = f; });
      setAnomalyIds(map);
    }).catch(() => setAnomalyIds({}));
  }, []);

  // Autopair: if user types a party name that fuzzy-matches a sibling tenant, offer to tick intercompany.
  useEffect(() => {
    if (!party || party.length < 2 || siblings.length === 0) { setAutoPairHint(null); return; }
    const lp = party.toLowerCase();
    const match = siblings.find((s) =>
      (s.name || "").toLowerCase().includes(lp) ||
      lp.includes((s.name || "").toLowerCase())
    );
    if (match && !isIntercompany) {
      setAutoPairHint(match);
    } else {
      setAutoPairHint(null);
    }
  }, [party, siblings, isIntercompany]);

  const acceptAutoPair = () => {
    if (!autoPairHint) return;
    setIsIntercompany(true);
    setCounterpartyTenantId(autoPairHint.tenant_id);
    toast.success(`Auto-paired with ${autoPairHint.name} — will be eliminated in consolidation`);
    setAutoPairHint(null);
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/invoices", {
        type, party, amount: Number(amount), description: desc, vat_category: vatCategory,
        is_intercompany: isIntercompany,
        counterparty_tenant_id: isIntercompany && counterpartyTenantId ? counterpartyTenantId : null,
      });
      toast.success(isIntercompany ? "Intercompany invoice created (will be eliminated in consolidation)" : "Invoice created");
      setParty(""); setAmount(""); setDesc("");
      setIsIntercompany(false); setCounterpartyTenantId("");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const setStatus = async (id, s) => {
    await api.patch(`/finance/invoices/${id}?status=${s}`);
    onChange();
  };

  const badgeColor = (s) => ({
    open: "bg-zinc-100 text-zinc-700",
    paid: "bg-emerald-50 text-emerald-700",
    overdue: "bg-red-50 text-[#D0021B]",
    draft: "bg-amber-50 text-amber-700",
  }[s] || "bg-zinc-100");

  return (
    <div>
      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-[80px_1fr_120px_140px_1fr_auto] gap-px bg-zinc-200 border-b border-zinc-950" data-testid="invoice-form">
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="invoice-type-select" className="bg-white px-4 py-3 text-sm outline-none">
          <option value="AR">AR</option>
          <option value="AP">AP</option>
        </select>
        <input value={party} onChange={(e) => setParty(e.target.value)} required placeholder="Party (customer/vendor)" data-testid="invoice-party-input" className="bg-white px-4 py-3 text-sm outline-none" />
        <select value={vatCategory} onChange={(e) => setVatCategory(e.target.value)} data-testid="invoice-vat-category" className="bg-white px-4 py-3 text-sm outline-none" title="Oman VAT category">
          <option value="standard">VAT 5%</option>
          <option value="zero_rated">Zero-rated</option>
          <option value="exempt">Exempt</option>
        </select>
        <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required placeholder="Amount (net)" data-testid="invoice-amount-input" className="bg-white px-4 py-3 text-sm outline-none tnum" />
        <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description" className="bg-white px-4 py-3 text-sm outline-none" />
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 hover:bg-[#FF3E00] flex items-center justify-center gap-1" data-testid="invoice-create-btn">
          <Plus size={14} /> ADD
        </button>
      </form>
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 border-b border-zinc-200 bg-zinc-50 text-xs">
        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="invoice-intercompany-toggle">
          <input
            type="checkbox"
            checked={isIntercompany}
            onChange={(e) => setIsIntercompany(e.target.checked)}
            className="accent-[#FF3E00]"
            data-testid="invoice-intercompany-checkbox"
          />
          <span className="font-mono tracking-wider">INTERCOMPANY (eliminated in consolidation)</span>
        </label>
        {isIntercompany && (
          <select
            value={siblings.some((s) => s.tenant_id === counterpartyTenantId) ? counterpartyTenantId : ""}
            onChange={(e) => setCounterpartyTenantId(e.target.value)}
            className="border border-zinc-950 bg-white px-2 py-1 text-xs"
            data-testid="invoice-counterparty-input"
          >
            <option value="">— pick sibling tenant —</option>
            {siblings.map((s) => <option key={s.tenant_id} value={s.tenant_id}>{s.name} ({s.tenant_id})</option>)}
          </select>
        )}
        {isIntercompany && !siblings.some((s) => s.tenant_id === counterpartyTenantId) && (
          <input
            value={counterpartyTenantId}
            onChange={(e) => setCounterpartyTenantId(e.target.value)}
            placeholder="or type tenant_id manually"
            className="border border-zinc-950 bg-white px-2 py-1 text-xs w-56"
            data-testid="invoice-counterparty-manual"
          />
        )}
      </div>
      {autoPairHint && (
        <div className="px-4 py-2 border-b border-amber-200 bg-amber-50 flex items-center justify-between text-xs" data-testid="invoice-autopair-hint">
          <span>
            <span className="font-mono tracking-wider text-amber-800">AUTOPAIR:</span>{" "}
            "{party}" looks like your group tenant <b>{autoPairHint.name}</b>. Tick intercompany?
          </span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={acceptAutoPair} className="text-[10px] font-mono bg-amber-800 text-white px-3 py-1 hover:bg-amber-900" data-testid="invoice-autopair-accept">YES · TICK IC</button>
            <button type="button" onClick={() => setAutoPairHint(null)} className="text-[10px] font-mono text-amber-800 hover:underline" data-testid="invoice-autopair-dismiss">Dismiss</button>
          </div>
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">Invoice #</th>
            <th className="text-left px-4 py-2.5">Type</th>
            <th className="text-left px-4 py-2.5">Party</th>
            <th className="text-right px-4 py-2.5">Amount</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="text-left px-4 py-2.5">Due</th>
            <th className="w-32" />
          </tr>
        </thead>
        <tbody data-testid="invoices-tbody">
          {invoices.map((i) => (
            <tr key={i.id} className="border-b border-zinc-100 hover:bg-zinc-50">
              <td className="px-4 py-2.5 tnum text-xs">{i.invoice_no}</td>
              <td className="px-4 py-2.5"><span className="text-[10px] font-mono px-1.5 py-0.5 border border-zinc-300">{i.type}</span>{i.is_intercompany && <span className="ml-1 text-[10px] font-mono px-1.5 py-0.5 bg-amber-100 text-amber-800" data-testid={`invoice-inter-badge-${i.id}`}>INTER</span>}{anomalyIds[i.id] && <span className="ml-1 text-[10px] font-mono px-1.5 py-0.5 bg-red-100 text-[#D0021B]" title={anomalyIds[i.id].reasons?.join(' · ')} data-testid={`invoice-anomaly-badge-${i.id}`}>ANOMALY · S{anomalyIds[i.id].severity}</span>}</td>
              <td className="px-4 py-2.5">{i.party}</td>
              <td className="px-4 py-2.5 text-right tnum font-semibold">{fmt$(i.amount, i.currency || cur)}</td>
              <td className="px-4 py-2.5"><span className={`text-[10px] px-1.5 py-0.5 ${badgeColor(i.status)}`}>{i.status?.toUpperCase()}</span></td>
              <td className="px-4 py-2.5 text-zinc-500 text-xs">{i.due_date || "—"}</td>
              <td className="px-4 py-2.5">
                {i.status !== "paid" && (
                  <button onClick={() => setStatus(i.id, "paid")} className="text-[10px] font-mono text-emerald-700 hover:underline" data-testid={`invoice-mark-paid-${i.id}`}>
                    MARK PAID
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ============================================================
// Voice-driven Journal Entry (uses OpenAI Whisper via backend)
// ============================================================
function VoiceJournalPanel({ onApply }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 500) { toast.error("Recording too short — try again"); return; }
        setBusy(true);
        const fd = new FormData();
        fd.append("audio", blob, "voice-journal.webm");
        try {
          const r = await api.post("/finance/journals/voice", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setResult(r.data);
          toast.success("Transcribed — review draft below");
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Transcription failed");
        } finally { setBusy(false); }
      };
      mr.start();
      setRecording(true);
    } catch (err) {
      toast.error(`Cannot start recording: ${err.message}`);
    }
  };

  const stop = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  return (
    <div className="border-b border-zinc-950 bg-zinc-50" data-testid="voice-journal-panel">
      <div className="bg-zinc-950 text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Microphone size={14} weight="fill" className="text-[#FF3E00]" />
          <span className="font-mono text-[10px] tracking-widest">VOICE JOURNAL · Speak debit & credit lines</span>
        </div>
        <span className="font-mono text-[10px] text-zinc-300 hidden md:inline">
          e.g. "memo office supplies, debit 5900 for 250, credit 1000 for 250"
        </span>
      </div>
      <div className="p-4 flex flex-wrap items-center gap-3">
        {!recording ? (
          <button
            type="button"
            onClick={start}
            disabled={busy}
            className="flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 text-xs font-semibold hover:bg-[#FF3E00] disabled:opacity-40"
            data-testid="voice-journal-start"
          >
            <Microphone size={14} weight="fill" /> START RECORDING
          </button>
        ) : (
          <button
            type="button"
            onClick={stop}
            className="flex items-center gap-2 bg-[#D0021B] text-white px-4 py-2 text-xs font-semibold pulse-dot"
            data-testid="voice-journal-stop"
          >
            <StopCircle size={14} weight="fill" /> STOP & TRANSCRIBE
          </button>
        )}
        {busy && <span className="text-xs font-mono text-zinc-500 pulse-dot">TRANSCRIBING…</span>}
      </div>
      {result && (
        <div className="border-t border-zinc-200 p-4 space-y-3 bg-white" data-testid="voice-journal-result">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">TRANSCRIPT</div>
            <div className="text-sm italic text-zinc-700 border-l-2 border-zinc-950 pl-3" data-testid="voice-journal-transcript">"{result.transcript}"</div>
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">DRAFT · MEMO: {result.draft.memo}</div>
            <table className="w-full text-sm border border-zinc-200">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="text-left px-2 py-1">Account</th>
                  <th className="text-right px-2 py-1">Debit</th>
                  <th className="text-right px-2 py-1">Credit</th>
                </tr>
              </thead>
              <tbody>
                {result.draft.lines.map((l, i) => (
                  <tr key={`${l.account_code}-${i}`} className="border-b border-zinc-100">
                    <td className="px-2 py-1 font-mono text-xs">{l.account_code}</td>
                    <td className="px-2 py-1 text-right tnum">{l.debit || "—"}</td>
                    <td className="px-2 py-1 text-right tnum">{l.credit || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className={result.draft.balanced ? "text-emerald-700" : "text-[#D0021B]"} data-testid="voice-journal-balanced">
                {result.draft.balanced ? "BALANCED ✓" : `UNBALANCED · DR ${result.draft.total_debit} / CR ${result.draft.total_credit}`}
              </span>
              <span className="text-zinc-500">{result.hint}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { onApply?.(result.draft); setResult(null); }}
              disabled={result.draft.lines.length === 0}
              className="flex items-center gap-1 bg-zinc-950 text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#FF3E00] disabled:opacity-40"
              data-testid="voice-journal-apply"
            >
              <MagicWand size={12} weight="fill" /> APPLY TO FORM
            </button>
            <button
              type="button"
              onClick={() => setResult(null)}
              className="text-xs font-mono text-zinc-500 hover:text-zinc-950"
              data-testid="voice-journal-discard"
            >Discard</button>
          </div>
        </div>
      )}
    </div>
  );
}
